/**
 * Progressive LOD (level-of-detail) helper.
 *
 * Goal: a HEAVY baked GLB (a 4K-textured Bungalow colorway is ~7-23 MB and
 * decodes to ~800 MB of VRAM) takes many seconds to download + decode on a real
 * phone connection — so the shopper stares at a static cover the whole time. A
 * progressive load fixes that: load a tiny proxy (X.lod.glb, same geometry with
 * 256px textures, ~0.5 MB) FIRST so the model is on screen and spinnable in ~1s,
 * then stream the full X.glb in behind, swap it in, and dispose the proxy.
 *
 * CONTRACT (exercised by tools/goalhard-lod-probe.mjs — do not drift):
 *   - URL `?lod=0` DISABLES LOD (load the full model directly, today's path).
 *     Absent or `?lod=1` ENABLES it.
 *   - With LOD on, for a full url X.glb the engine loads X.lod.glb FIRST, shows it
 *     (window.__NEOVAA_SCENE_READY__ flips true HERE = first spinnable), then loads
 *     X.glb, swaps it in, and DISPOSES the proxy (proxy + full never both kept —
 *     the iOS mobile-cache budget must hold).
 *   - window.__NEOVAA_LOD_STAGE__ = 'proxy' then 'full'.
 *   - window.__NEOVAA_FULL_AT__ = performance.now() at the swap.
 *   - A model with NO X.lod.glb gracefully loads full directly (never a 404 /
 *     error). We know which proxies exist from a manifest fetched once, so we
 *     never request a missing proxy.
 */

/** Read the LOD enable flag from the URL. Absent or `?lod=1` → enabled; `?lod=0`
 *  → disabled (direct full load = the pre-LOD path). */
export function isLodEnabled(): boolean {
  if (typeof window === "undefined") return false;
  const raw = new URLSearchParams(window.location.search).get("lod");
  return raw !== "0";
}

/** Derive the proxy url for a full GLB url: X.glb → X.lod.glb. Returns null if the
 *  url doesn't end in .glb or already IS a proxy. */
export function proxyUrlFor(fullUrl: string): string | null {
  if (!fullUrl.endsWith(".glb") || fullUrl.endsWith(".lod.glb")) return null;
  return fullUrl.replace(/\.glb$/, ".lod.glb");
}

/** Available-proxy set, by full-GLB basename (e.g. "large2-limestone"). Populated
 *  once from the manifest; `null` until loaded (treated as "unknown → no proxy"). */
let availableProxies: Set<string> | null = null;
/** Basenames that have a <name>.m.glb MOBILE tier (1024px textures). On phones
 *  this variant is served AS the full-quality tier for heavy models: a chaise/
 *  U-shape's ~24 4K maps decode to ~2 GB — over the iOS tab budget on their own
 *  (Alex's repeated crashes, 2026-06-11) — while the 1024px tier is ~135 MB.
 *  Desktop always gets the true 4K full. KTX2 will retire this tier. */
let mobileTier: Set<string> | null = null;
let manifestPromise: Promise<void> | null = null;

/** When true, serve the light <name>.m.glb tier on EVERY device, not just phones.
 *  Set by the 360 configurator (main360.ts): there the live 3D is never the shown model
 *  — it's hidden behind the pre-rendered spin and used only for AR + the dimensions
 *  readout. On desktop, AR is a QR the phone resolves with its own model, so capping the
 *  desktop's hidden 3D to the mobile tier deletes a 15–28 MB wasted download with zero
 *  AR-quality impact. The 3D configurator never sets this → it keeps full 4K. */
let forceMobileTier = false;
export function setForceMobileTier(on: boolean): void { forceMobileTier = on; }

/** Basename of a GLB url WITHOUT the .glb / .lod.glb / .m.glb suffix or path. */
function basenameOf(url: string): string {
  const file = url.split("/").pop() ?? url;
  return file.replace(/\.(lod|m)\.glb$/, "").replace(/\.glb$/, "");
}

/**
 * Fetch the LOD proxy manifest ONCE (idempotent). The manifest lives next to the
 * GLBs and lists the basenames that have a proxy, so we can decide "is there a
 * proxy for this model?" WITHOUT a speculative request that would 404 for models
 * with no proxy. A missing/failed manifest just leaves the set empty → LOD is a
 * graceful no-op (full loads directly). Best-effort; never throws.
 *
 * @param sampleFullUrl any full GLB url for the product, used to locate the
 *   manifest (same directory). e.g. "/perf-assets/bungalow-studio/large2-limestone.glb"
 */
export function ensureLodManifest(sampleFullUrl: string): Promise<void> {
  if (availableProxies) return Promise.resolve();
  if (manifestPromise) return manifestPromise;
  const dir = sampleFullUrl.slice(0, sampleFullUrl.lastIndexOf("/") + 1);
  const manifestUrl = `${dir}lod-manifest.json`;
  manifestPromise = fetch(manifestUrl)
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
    .then((j: { proxies?: string[]; mobileTier?: string[] }) => {
      availableProxies = new Set(Array.isArray(j.proxies) ? j.proxies : []);
      mobileTier = new Set(Array.isArray(j.mobileTier) ? j.mobileTier : []);
    })
    .catch(() => {
      // No manifest (or unreachable) → no known proxies. LOD becomes a no-op for
      // this product: the full GLB loads directly, exactly as before LOD existed.
      availableProxies = new Set();
      mobileTier = new Set();
    });
  return manifestPromise;
}

/** The EFFECTIVE full-quality url for this device: on mobile, a heavy model with
 *  a 1024px tier (per the manifest) serves <name>.m.glb as its "full"; everything
 *  else (and all of desktop) keeps the true 4K <name>.glb. Call only after
 *  ensureLodManifest() resolved. `isMobile` is passed in so this module stays
 *  free of device-detection imports. */
export function effectiveFullUrl(fullUrl: string, isMobile: boolean): string {
  if ((!isMobile && !forceMobileTier) || !mobileTier || !mobileTier.has(basenameOf(fullUrl))) return fullUrl;
  return fullUrl.replace(/\.glb$/, ".m.glb");
}

/** True iff a proxy is known to exist for this full GLB url (per the manifest).
 *  Call only after ensureLodManifest() has resolved. */
export function hasProxy(fullUrl: string): boolean {
  return availableProxies != null && availableProxies.has(basenameOf(fullUrl));
}

/** Set the LOD stage instrumentation flag the probe reads. */
export function setLodStage(stage: "proxy" | "full"): void {
  if (typeof window === "undefined") return;
  (window as unknown as { __NEOVAA_LOD_STAGE__?: string }).__NEOVAA_LOD_STAGE__ = stage;
  if (stage === "full") {
    (window as unknown as { __NEOVAA_FULL_AT__?: number }).__NEOVAA_FULL_AT__ =
      typeof performance !== "undefined" ? performance.now() : Date.now();
  }
}

/** Reset the LOD instrumentation at the start of a fresh boot. */
export function resetLodInstrumentation(): void {
  if (typeof window === "undefined") return;
  const w = window as unknown as { __NEOVAA_LOD_STAGE__?: string; __NEOVAA_FULL_AT__?: number };
  w.__NEOVAA_LOD_STAGE__ = undefined;
  w.__NEOVAA_FULL_AT__ = 0;
}

/** Test-only: clear the cached manifest so a fresh page-context re-fetches it.
 *  (No effect in production — the engine fetches once per page load.) */
export function __resetLodManifestForTest(): void {
  availableProxies = null;
  manifestPromise = null;
}
